import type {WorkstreamMetric} from 'client';
import {isSameLdap} from 'views/workstreams/detail/scopeRouting';
import {
  getOutcomeStatus,
  isTerminalOutcomeStatus,
} from 'views/workstreams/detail/WorkstreamOutcomeStatus';
import {getOutcomeMetaParts} from 'views/workstreams/detail/WorkstreamRedesignSections';
import {getFullWorkstreamMetrics} from 'views/workstreams/utils';

import {isArchived, isClosed, type ScanWorkstream} from './WorkstreamScanList';

export const OUTCOMES_OVERDUE_LABEL = 'Outcomes overdue';

// Workstream roles that make all of its outcomes yours, as the overdue-outcomes
// notification counts them (WORKSTREAM_DRI_ROLES in helpers/outcome_overdue_action_items.py).
// Read from the row's members, since the rail's role folds "owner" into Working team.
const OWNING_ROLES = new Set(['dri', 'owner']);

const ownsWorkstream = (row: ScanWorkstream, myLdap: string | null | undefined) =>
  row.employee_associations.some(
    (association) =>
      OWNING_ROLES.has(association.dri) && isSameLdap(association.employee.ldap, myLdap)
  );

/**
 * Your overdue outcomes on a workstream, the ones the overdue-outcomes
 * notification is about: open milestones past their date that you own, or any
 * of them on a workstream you're DRI or owner of. Archived or closed
 * workstreams have none. "Overdue" is the cards' own (the red date), so the
 * section and the open card agree.
 */
export const myOverdueOutcomes = (
  row: ScanWorkstream,
  myLdap: string | null | undefined
): WorkstreamMetric[] => {
  if (!myLdap || isArchived(row) || isClosed(row)) return [];
  const allMine = ownsWorkstream(row, myLdap);
  return getFullWorkstreamMetrics(row.metrics).filter(
    (metric) =>
      metric.type !== 'metric' &&
      !isTerminalOutcomeStatus(getOutcomeStatus(metric)) &&
      getOutcomeMetaParts(metric).isOverdue &&
      (allMine || isSameLdap(metric.owner?.ldap, myLdap))
  );
};

// When a workstream's oldest overdue outcome was due, for most overdue first.
const oldestDueTime = (outcomes: WorkstreamMetric[]) =>
  Math.min(
    ...outcomes.map((outcome) => new Date(outcome.complete_date ?? '').getTime() || Infinity)
  );

/**
 * Rows with outcomes of yours overdue (the one whose oldest is most overdue
 * first), and the rest in their order.
 */
export const splitOutcomesOverdue = <T extends ScanWorkstream>(
  rows: T[],
  myLdap: string | null | undefined
): {overdue: T[]; rest: T[]} => {
  const overdue = rows
    .map((row) => ({row, outcomes: myOverdueOutcomes(row, myLdap)}))
    .filter(({outcomes}) => outcomes.length > 0)
    .sort(
      (a, b) =>
        oldestDueTime(a.outcomes) - oldestDueTime(b.outcomes) ||
        a.row.name.localeCompare(b.row.name)
    )
    .map(({row}) => row);
  const overdueIds = new Set(overdue.map((row) => row.id));
  return {overdue, rest: rows.filter((row) => !overdueIds.has(row.id))};
};
